import { toast } from '@heroui/react';
/**
 * 空间应用授权 - 添加用户弹窗
 * - visibility：为未公开应用添加 USER 可见性规则
 * - admin：添加应用管理员（写入 space_apps.admin）
 */
import * as React from 'react';
import { useAppSelector } from '@/stores/hooks';
import { trpc } from '@/lib/trpc';
import {
  Avatar,
  Button,
  Checkbox,
  Description,
  Input,
  Modal,
} from '@heroui/react';
import {
  IconSearch as Search,
  IconChevronLeft as ChevronLeft,
  IconChevronRight as ChevronRight,
  IconChevronsLeft as ChevronsLeft,
  IconChevronsRight as ChevronsRight,
  IconX as X,
} from '@tabler/icons-react';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const PAGE_SIZE = 10;

export type MemberPickerItem = {
  id: string;
  spaceId?: string | null;
  userId?: string | null;
  user?: {
    id: string;
    username?: string | null;
    nickname?: string | null;
    avatarUrl?: string | null;
  } | null;
};

export interface AddPermissionUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 当前应用 id */
  appId: string;
  /** 空间应用记录 id（admin 模式必填） */
  spaceAppId?: string;
  /** visibility：添加可见性规则；admin：添加应用管理员 */
  mode?: 'visibility' | 'admin';
  /** 已有管理员 user id（admin 模式用于过滤） */
  existingAdminUserIds?: string[];
  onSuccess?: () => void;
}

export function AddPermissionUserModal({
  open,
  onOpenChange,
  appId,
  spaceAppId,
  mode = 'visibility',
  existingAdminUserIds = [],
  onSuccess,
}: AddPermissionUserModalProps) {
  const user = useAppSelector((state) => state.auth.user);
  const spaceId = user?.spaces?.[0]?.id;
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [keyword, setKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selectedMembers, setSelectedMembers] = React.useState<MemberPickerItem[]>([]);

  const membersQueryEnabled = open && !!spaceId && isHydrated && isAuthenticated;

  const { data: spaceMembersPage, isLoading: membersLoading } = trpc.spaceMembers.list.useQuery(
    {
      page,
      pageSize: PAGE_SIZE,
      keyword: keyword.trim() || undefined,
      sortBy: 'createdAt',
      sortOrder: 'asc',
    },
    { enabled: membersQueryEnabled }
  );

  const { data: existingVisibilityUsers, isLoading: visibilityUsersLoading } =
    trpc.spaceAppVisibility.listAll.useQuery(
      {
        appId,
        targetType: 'USER',
        page: 1,
        pageSize: 9999,
      },
      { enabled: open && mode === 'visibility' && !!appId && membersQueryEnabled }
    );

  const utils = trpc.useUtils();

  const createVisibilityMutation = trpc.spaceAppVisibility.create.useMutation();

  const addAdminsMutation = trpc.spaceApps.addAdmins.useMutation({
    onSuccess: () => {
      toast.success('已添加管理员');
      onOpenChange(false);
      resetState();
      onSuccess?.();
      if (spaceId) void utils.spaceApps.listAll.invalidate({ spaceId });
    },
    onError: (e) => toast.danger(e.message || '添加管理员失败'),
  });

  const resetState = React.useCallback(() => {
    setKeyword('');
    setPage(1);
    setSelectedMembers([]);
  }, []);

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) resetState();
      onOpenChange(next);
    },
    [onOpenChange, resetState]
  );

  const adminExcludeSet = React.useMemo(
    () => new Set(existingAdminUserIds),
    [existingAdminUserIds]
  );

  const visibilityExcludeSet = React.useMemo(
    () =>
      new Set(
        (existingVisibilityUsers ?? [])
          .map((row) => row.targetId)
          .filter((id): id is string => Boolean(id))
      ),
    [existingVisibilityUsers]
  );

  const excludeSet = mode === 'admin' ? adminExcludeSet : visibilityExcludeSet;

  const items = React.useMemo(() => {
    const raw = (spaceMembersPage?.items ?? []) as MemberPickerItem[];
    return raw.filter((m) => {
      const uid = m.userId ?? m.user?.id;
      return uid && !excludeSet.has(uid);
    });
  }, [spaceMembersPage?.items, excludeSet]);

  const totalPages = spaceMembersPage?.totalPages ?? 0;

  const membersLoadingState =
    membersLoading || (mode === 'visibility' && visibilityUsersLoading);

  const selectedIds = React.useMemo(() => new Set(selectedMembers.map((m) => m.id)), [selectedMembers]);

  const toggleMember = React.useCallback((item: MemberPickerItem) => {
    setSelectedMembers((prev) => {
      const has = prev.some((p) => p.id === item.id);
      if (has) return prev.filter((p) => p.id !== item.id);
      return [...prev, { ...item }];
    });
  }, []);

  const removeMember = React.useCallback((id: string) => {
    setSelectedMembers((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const toggleSelectAllCurrentPage = React.useCallback(
    (checked: boolean) => {
      setSelectedMembers((prev) => {
        const currentPageIds = new Set(items.map((i) => i.id));
        if (checked) {
          const existingIds = new Set(prev.map((p) => p.id));
          const toAdd = items.filter((i) => !existingIds.has(i.id));
          return [...prev, ...toAdd.map((i) => ({ ...i }))];
        }
        return prev.filter((p) => !currentPageIds.has(p.id));
      });
    },
    [items]
  );

  const handleSubmit = React.useCallback(async () => {
    if (selectedMembers.length === 0) return;

    if (mode === 'visibility') {
      const spaceIdFromMember = selectedMembers[0]?.spaceId ?? spaceId;
      if (!spaceIdFromMember) {
        toast.danger('缺少空间信息');
        return;
      }

      const payload = selectedMembers
        .map((m) => {
          const targetId = m.userId ?? m.user?.id;
          if (!targetId) return null;
          return {
            spaceId: m.spaceId ?? spaceIdFromMember,
            appId,
            targetType: 'USER' as const,
            targetId,
          };
        })
        .filter(Boolean) as Array<{
        spaceId: string;
        appId: string;
        targetType: 'USER';
        targetId: string;
      }>;

      try {
        await Promise.all(payload.map((item) => createVisibilityMutation.mutateAsync(item)));
        toast.success(`已为 ${payload.length} 个用户添加可见性`);
        onOpenChange(false);
        resetState();
        onSuccess?.();
        void utils.spaceAppVisibility.list.invalidate({ appId });
      } catch (e) {
        const message = e instanceof Error ? e.message : '批量添加失败';
        toast.danger(message);
      }
      return;
    }

    if (!appId) {
      toast.danger('缺少应用信息');
      return;
    }

    const userIds = selectedMembers
      .map((m) => m.userId ?? m.user?.id)
      .filter((id): id is string => Boolean(id));

    if (userIds.length === 0) {
      toast.danger('未选择有效用户');
      return;
    }

    addAdminsMutation.mutate({ appId, userIds });
  }, [
    selectedMembers,
    mode,
    spaceId,
    appId,
    createVisibilityMutation,
    addAdminsMutation,
    onOpenChange,
    resetState,
    onSuccess,
    utils.spaceAppVisibility.list,
  ]);

  const displayName = (item: MemberPickerItem) =>
    item.user?.nickname || item.user?.username || `用户 ${item.userId ?? item.user?.id ?? ''}`;

  const allOnPageSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const isPending = createVisibilityMutation.isPending || addAdminsMutation.isPending;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden"
            key={open ? (appId ? `add-${mode}-${appId}` : `add-${mode}`) : 'closed'}
          >
            <Modal.Header className="shrink-0 gap-3">
              <div className="flex min-w-0 flex-col gap-1">
                <Modal.Heading className="text-xl font-medium">
                  {mode === 'admin' ? '添加管理员' : '添加可见用户'}
                </Modal.Heading>
                <Description className="sr-only">
                  {mode === 'admin' ? '选择成员设为应用管理员' : '选择成员加入应用可见范围'}
                </Description>
              </div>
            </Modal.Header>

            <Modal.Body className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex min-h-[360px] flex-1 flex-row overflow-hidden rounded-3xl bg-surface-secondary">
                <div className="border-border flex w-3/5 min-w-0 flex-col overflow-hidden border-r">
                  <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4">
                    <Checkbox
                      isSelected={allOnPageSelected}
                      onChange={() => toggleSelectAllCurrentPage(!allOnPageSelected)}
                      aria-label="全选本页"
                    >
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                    </Checkbox>
                    <h4 className="text-sm font-medium">选择用户</h4>
                  </div>
                  <div className="flex h-14 shrink-0 items-center">
                    <div className="relative min-w-0 flex-1 px-4">
                      <Search className="text-muted-foreground pointer-events-none absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2" />
                      <Input
                        className="pl-10"
                        placeholder="输入用户名或昵称搜索"
                        value={keyword}
                        aria-label="搜索成员"
                        onChange={(e) => {
                          setKeyword(e.target.value);
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden py-2">
                    <div className="min-h-[120px] flex-1 space-y-1 overflow-y-auto px-4">
                      {membersLoadingState ? (
                        <p className="text-muted-foreground text-sm">加载中…</p>
                      ) : (
                        items.map((item) => {
                          const checked = selectedIds.has(item.id);
                          return (
                            <label
                              key={item.id}
                              className="hover:bg-accent/10 flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5"
                            >
                              <Checkbox
                                isSelected={checked}
                                onChange={() => toggleMember(item)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox.Control>
                                  <Checkbox.Indicator />
                                </Checkbox.Control>
                              </Checkbox>
                              <Avatar className="h-8 w-8 shrink-0" variant="soft">
                                {item.user?.avatarUrl ? (
                                  <Avatar.Image src={item.user.avatarUrl} alt={displayName(item)} />
                                ) : null}
                                <Avatar.Fallback>
                                  {(item.user?.nickname || item.user?.username || '?').charAt(0).toUpperCase()}
                                </Avatar.Fallback>
                              </Avatar>
                              <div className="flex min-w-0 flex-col">
                                <span className="text-sm">
                                  {item.user?.nickname ? item.user.nickname : '昵称未设置'}
                                </span>
                                <span className="text-muted-foreground truncate text-xs">
                                  {item.user?.username ?? '—'}
                                </span>
                              </div>
                            </label>
                          );
                        })
                      )}
                      {!membersLoadingState && items.length === 0 ? (
                        <p className="text-muted-foreground text-sm">暂无可选成员</p>
                      ) : null}
                    </div>
                    <div className="text-muted-foreground flex shrink-0 items-center justify-between gap-2 border-t border-border px-4 pt-2 text-sm">
                      <span>
                        共{totalPages}页，当前: {page}/{totalPages || 1}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="outline"
                          className="min-w-8"
                          isDisabled={page <= 1}
                          onPress={() => setPage(1)}
                          aria-label="第一页"
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="outline"
                          className="min-w-8"
                          isDisabled={page <= 1}
                          onPress={() => setPage((p) => Math.max(1, p - 1))}
                          aria-label="上一页"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="outline"
                          className="min-w-8"
                          isDisabled={page >= totalPages}
                          onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                          aria-label="下一页"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="outline"
                          className="min-w-8"
                          isDisabled={page >= totalPages}
                          onPress={() => setPage(totalPages)}
                          aria-label="最后一页"
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <div className="border-border flex h-12 shrink-0 items-center border-b px-4">
                    <h4 className="text-sm font-medium">已选择</h4>
                  </div>
                  <div className="flex h-14 shrink-0 items-center px-4">
                    <p className="text-muted-foreground text-sm">{selectedMembers.length}个用户</p>
                  </div>
                  <div className="flex-1 space-y-1 overflow-y-auto px-4 py-2">
                    {selectedMembers.map((m) => (
                      <div
                        key={m.id}
                        className="bg-accent/10 flex items-center gap-2 rounded-md px-2 py-1.5"
                      >
                        <Avatar className="h-8 w-8 shrink-0" variant="soft">
                          {m.user?.avatarUrl ? (
                            <Avatar.Image src={m.user.avatarUrl} alt={displayName(m)} />
                          ) : null}
                          <Avatar.Fallback>
                            {(m.user?.nickname || m.user?.username || '?').charAt(0).toUpperCase()}
                          </Avatar.Fallback>
                        </Avatar>
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="text-sm">{m.user?.nickname ? m.user.nickname : '昵称未设置'}</span>
                          <span className="text-muted-foreground truncate text-xs">{m.user?.username ?? '—'}</span>
                        </div>
                        <Button
                          type="button"
                          isIconOnly
                          size="sm"
                          variant="ghost"
                          className="shrink-0"
                          onPress={() => removeMember(m.id)}
                          aria-label="移除"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={() => handleModalOpenChange(false)}>
                取消
              </Button>
              <Button
                variant="primary"
                onPress={() => handleSubmit()}
                isDisabled={isPending || selectedMembers.length === 0}
              >
                {isPending ? '提交中…' : '确定'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
